import React, { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { useSetting } from "../hooks/useSetting";
import { showToast } from "../../../core/utils/alert";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

// Validation schema
const validationSchema = yup.object().shape({
  key: yup.string().required("Key is required"),
  value: yup.string().required("Value is required"),
  description: yup.string(),
});

const AddSetting = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const { addNewSetting, updateSetting, isAdding, isUpdating, addError, useGetSettingById } = useSetting();
  const { id } = useParams<{ id: string }>();
  
  const { 
    register, 
    handleSubmit, 
    reset, 
    formState: { errors, isDirty, dirtyFields },
    setValue
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onBlur", // Validate on blur (when switching fields)
    defaultValues: {
      key: '',
      value: '',
      description: ''
    }
  });

  const settingData = useGetSettingById(id || '');
  const isEdit = !!id && !!settingData;

  useEffect(() => {
    if (id && settingData) {
      // Update form with existing data
      setValue("key", settingData.key || '');
      setValue("value", settingData.value || '');
      setValue("description", settingData.description || '');
    } else {
      // Reset form for new setting
      reset({
        key: '',
        value: '',
        description: ''
      });
    }
  }, [id, settingData, setValue, reset]);

  const onSubmit = async (data: any) => {
    const payload = {
      key: data.key,
      value: data.value,
      description: data.description
    };

    let result;
    if (isEdit && id) {
      result = await updateSetting(id, payload);
    } else {
      result = await addNewSetting(payload);
    }

    if (result?.success) {
      navigate(routes.settingList);
      showToast({
        title: "Success",
        text: "Setting saved successfully",
        icon: "success",
      });
    }
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content content-two">
          {/* Updated Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">{isEdit ? 'Edit' : 'Add'} Setting</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.settingList}>Settings</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEdit ? 'Edit' : 'Add'} Setting
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}
          <div className="row">
            <div className="col-md-12">
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Setting Core Information */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-calendar-event fs-16" />
                      </span>
                      <h4 className="text-dark">Setting Details</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">Key <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className={`form-control ${errors.key ? 'is-invalid' : ''}`}
                            placeholder="Enter key"
                            {...register("key")}
                          />
                          {errors.key && (
                            <div className="invalid-feedback">{errors.key.message}</div>
                          )}
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Value <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className={`form-control ${errors.value ? 'is-invalid' : ''}`}
                            placeholder="Enter value"
                            {...register("value")}
                          />
                          {errors.value && (
                            <div className="invalid-feedback">{errors.value.message}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="mb-3">
                          <label className="form-label">Description</label>
                          <textarea
                            className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                            rows={4}
                            placeholder="Enter setting description"
                            {...register("description")}
                          />
                          {errors.description && (
                            <div className="invalid-feedback">{errors.description.message}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-end mt-4">
                  <Link to={routes.settingList} className="btn btn-light me-3">
                    Cancel
                  </Link>
                  <button type="submit" className="btn btn-primary" disabled={isAdding || isUpdating}>
                    {isAdding ? 'Saving...' : isUpdating ? 'Updating...' : isEdit ? 'Update Setting' : 'Create Setting'}
                  </button>
                </div>
              </form>
            </div>
          </div>
          {addError && (
            <div className="alert alert-danger mt-3">
              {addError}
            </div>
          )}
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default AddSetting;
